import { createContext, useContext, useMemo } from 'react';

/**
 * The ParentElementType should be kept to types that are often not viewable
 * on page load. The goal of this context is to track events better when they
 * occur within some hidden parentElement, such as a dialog
 * @sorted
 */
export type ParentElementType = 'dialog' | 'tab';

/** @sorted */
export type ParentElementName =
  | 'add-co-client-dialog'
  | 'add-household-member-dialog'
  | 'add-item-dialog'
  | 'add-mortgage-dialog'
  | 'add-secondary-portfolio-dialog'
  | 'address-dialog'
  | 'beneficiary-dialog'
  | 'advisor-dash-sidebar-reset-demo-household-dialog'
  | 'client-portal-change-password-dialog'
  | 'client-portal-report-issue-dialog'
  | 'client-since-dialog'
  | 'create-new-risk-questionnaire-task-dialog'
  | 'create-prospect-dialog'
  | 'create-salesforce-prospect-dialog'
  | 'connect-client-to-orion-button-manual-connection-dialog'
  | 'confirm-goal-delete-dialog'
  | 'confirm-note-exit-dialog'
  | 'confirm-skip-upload-dialog'
  | 'cost-basis-lots-dialog'
  | 'create-call-activity-dialog'
  | 'create-coi-call-log-dialog'
  | 'create-coi-meeting-dialog'
  | 'create-coi-note-dialog'
  | 'create-coi-rep-dialog'
  | 'create-company-call-log-dialog'
  | 'create-company-meeting-dialog'
  | 'create-company-note-dialog'
  | 'create-contact-call-log-dialog'
  | 'create-contact-meeting-dialog'
  | 'create-contact-note-dialog'
  | 'create-existing-opportunity-dialog'
  | 'create-general-opportunity-dialog'
  | 'create-meeting-dialog'
  | 'date-of-birth-dialog'
  | 'delete-activity-confirmation-dialog'
  | 'destructive-action-confirmation-dialog'
  | 'disable-mfa-dialog'
  | 'discovery-dialog'
  | 'edit-asset-dialog'
  | 'edit-coi-rep-dialog'
  | 'edit-dialpad-call-dialog'
  | 'edit-financial-account-dialog'
  | 'edit-household-member-dialog'
  | 'edit-investment-policy-modal'
  | 'edit-liability-dialog'
  | 'edit-existing-client-opportunity-dialog'
  | 'edit-existing-client-opportunity-row-dialog'
  | 'email-dialog'
  | 'email-reply-draft-dialog'
  | 'employment-info-dialog'
  | 'enable-mfa-dialog'
  | 'expandable-activity-content-card-expanded-dialog'
  | 'expanded-activity-dialog'
  | 'file-share-dialog'
  | 'form-dialog'
  | 'global-create-dialog'
  | 'create-fee-schedule-dialog'
  | 'fee-schedule-error-dialog'
  | 'guided-flow-client-account-setup-contact-dialog'
  | 'heldaway-account-delete-dialog'
  | 'heldaway-holding-detail-dialog'
  | 'investment-policy-modal'
  | 'investment-proposal-download-modal'
  | 'lead-alert-dialog'
  | 'lost-lead-dialog'
  | 'marital-status-dialog'
  | 'note-dialog'
  | 'phone-dialog'
  | 'portfolio-insights-dialog'
  | 'portfolio-item-remove-item-dialog'
  | 'prospect-email-from-lead-cta-dialog'
  | 'prospect-sms-from-lead-cta-dialog'
  | 'remove-item-dialog'
  | 'rename-account-dialog'
  | 'resend-email-confirmation-dialog'
  | 'rmd-dialog'
  | 'service-calendar-add-item-dialog'
  | 'service-calendar-change-service-item-dialog'
  | 'fee-schedule-edit-dialog'
  | 'fee-schedule-delete-dialog'
  | 'service-calendar-view-meeting-details-dialog'
  | 'service-item-detail-drawer'
  | 'share-household-with-advisor-group-dialog'
  | 'task-details-dialog'
  | 'take-risk-questionnaire-dialog'
  | 'update-account-balance-dialog'
  | 'update-household-stage-dialog'
  | 'update-item-value-dialog'
  | 'update-mortgage-dialog'
  | 'update-property-address-dialog'
  | 'upload-to-box-dialog'
  | 'upload-to-folder-controls-explore-dialog'
  | 'upload-to-folder-controls-upload-dialog'
  | 'view-portfolio-item-dialog'
  | 'view-risk-questionnaire-answers-dialog'
  | 'show-tags-dialog'
  | 'display-columns-dialog';

export type ParentElementViewContext = {
  parentElementType: ParentElementType;
  parentElementName: ParentElementName;
};
export type TrackedParentElementAddedProps = {
  elementName: ParentElementName;
};

const ParentElementViewContext = createContext<ParentElementViewContext | null>(null);

export function ParentElementViewProvider({
  children,
  parentElementName,
  parentElementType,
}: {
  readonly children: React.ReactNode;
  readonly parentElementName: ParentElementName;
  readonly parentElementType: ParentElementType;
}) {
  const parentElementViewContext = useMemo(
    () => ({
      parentElementName,
      parentElementType,
    }),
    [parentElementName, parentElementType],
  );
  return (
    <ParentElementViewContext.Provider value={parentElementViewContext}>{children}</ParentElementViewContext.Provider>
  );
}

export const useParentElementViewContext = () => {
  const parentElementViewContext = useContext(ParentElementViewContext);
  return parentElementViewContext;
};
